import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, BookOpen, HelpCircle, ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import api from "@/lib/api";

const MEDIA_RUBRIQUE_IDS = {
  live: "61b18e64-ce6f-48fa-a568-85b65fffebab",
  video: "e282b739-71bf-494c-a6d9-b8ddd28d40f3",
  quiz: "23ad1300-9ea9-42fe-908d-8bb686b61b89",
};

const SECTIONS = [
  { key: "live", label: "Lives", icon: Play, accent: "bg-ofa-red", description: "Retrouvez tous les Lives OFALycée : débats, témoignages et rencontres en direct." },
  { key: "video", label: "Vidéos", icon: Play, accent: "bg-ofa-blue", description: "Nos vidéos pédagogiques et reportages sur l'éducation internationale." },
  { key: "quiz", label: "Quiz", icon: HelpCircle, accent: "bg-ofa-green", description: "Testez vos connaissances sur l'éducation bilingue et internationale." },
];

export default function MediathequePage() {
  const [videos, setVideos] = useState([]);
  const [liveArticles, setLiveArticles] = useState([]);
  const [quizArticles, setQuizArticles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/videos"),
      api.get("/articles", { params: { rubrique: MEDIA_RUBRIQUE_IDS.live, limit: 100 } }),
      api.get("/articles", { params: { rubrique: MEDIA_RUBRIQUE_IDS.quiz, limit: 100 } }),
    ]).then(([vidRes, liveRes, quizRes]) => {
      setVideos(vidRes.data);
      setLiveArticles(liveRes.data.articles || []);
      setQuizArticles(quizRes.data.articles || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Build combined content list
  const allContent = [];
  liveArticles.forEach((a) => allContent.push({ ...a, _type: "live" }));
  videos.forEach((v) => allContent.push({ ...v, _type: v.category || "video" }));
  quizArticles.forEach((a) => allContent.push({ ...a, _type: "quiz" }));

  const filtered = filter === "all" ? allContent : allContent.filter((c) => c._type === filter);
  const sectionCounts = {
    live: liveArticles.length + videos.filter((v) => v.category === "live").length,
    video: videos.filter((v) => v.category === "video").length,
    quiz: quizArticles.length,
  };

  return (
    <>
      <SEO title="Médiathèque" description="Vidéos, Lives et ressources multimédia d'OFALycée." url="/mediatheque" />
      <div data-testid="mediatheque-page">
        {/* Hero */}
        <section className="bg-[#14274B] text-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <span className="text-sm font-mono tracking-widest uppercase text-white/50 mb-4 block">Médiathèque</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              Vidéos, Lives & Ressources
            </h1>
            <p className="text-lg text-white/60 max-w-2xl leading-relaxed mb-8">
              Découvrez nos contenus multimédia : lives en replay, vidéos pédagogiques
              et ressources pour accompagner votre parcours éducatif.
            </p>
            <a href="https://www.youtube.com/@ofalycee" target="_blank" rel="noopener noreferrer">
              <Button className="bg-ofa-red hover:bg-ofa-red/90 rounded-full px-6 text-white" data-testid="youtube-channel-btn">
                Notre chaîne YouTube <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>
        </section>

        {/* Sections overview */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const count = videos.filter((v) => v.category === s.key).length;
              return (
                <button
                  key={s.key}
                  onClick={() => setFilter(filter === s.key ? "all" : s.key)}
                  className={`p-6 border text-left transition-all ${filter === s.key ? "border-primary bg-primary/5 shadow-md" : "border-border hover:shadow-md"}`}
                  data-testid={`filter-${s.key}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${s.accent} flex items-center justify-center mb-4`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-heading text-lg mb-1">{s.label}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-2">{s.description}</p>
                  <span className="text-xs font-medium text-primary">{sectionCounts[s.key] || 0} contenu{(sectionCounts[s.key] || 0) > 1 ? "s" : ""}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Videos grid */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl md:text-3xl">
              {filter === "all" ? "Tous les contenus" : SECTIONS.find((s) => s.key === filter)?.label}
            </h2>
            {filter !== "all" && (
              <button onClick={() => setFilter("all")} className="text-sm text-primary hover:underline" data-testid="clear-filter">
                Voir tout
              </button>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (<div key={i} className="animate-pulse"><div className="aspect-video bg-muted rounded mb-4" /><div className="h-4 bg-muted rounded w-3/4" /></div>))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Play className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucun contenu pour le moment</h3>
              <p className="text-muted-foreground mb-6">Les vidéos et lives seront ajoutés prochainement.</p>
              <a href="https://www.youtube.com/@ofalycee" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full" data-testid="empty-youtube-btn">
                  Visitez notre chaîne YouTube <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((item) => {
                // YouTube video (from videos collection)
                if (item.youtube_id) {
                  return (
                    <div key={item.id} className="group" data-testid={`video-card-${item.id}`}>
                      <a href={item.youtube_url} target="_blank" rel="noopener noreferrer">
                        <div className="aspect-video bg-muted rounded overflow-hidden mb-4 relative">
                          <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <Play className="h-6 w-6 text-[#14274B] ml-1" fill="currentColor" />
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-mono tracking-wider uppercase text-primary mb-1 block">
                          {item.category === "live" ? "Live" : "Vidéo"}
                        </span>
                        <h3 className="font-heading text-lg group-hover:text-primary transition-colors">{item.title}</h3>
                        {item.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.description}</p>}
                      </a>
                    </div>
                  );
                }
                // Article (Live or Quiz from articles collection)
                return (
                  <div key={item.id} className="group" data-testid={`media-article-${item.id}`}>
                    <Link to={`/article/${item.slug}`}>
                      <div className="aspect-video bg-muted rounded overflow-hidden mb-4 relative">
                        {item.featured_image ? (
                          <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#14274B]">
                            {item._type === "quiz" ? <HelpCircle className="h-12 w-12 text-white/50" /> : <Play className="h-12 w-12 text-white/50" />}
                          </div>
                        )}
                        {item._type === "live" && (
                          <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-white/80 flex items-center justify-center">
                              <Play className="h-6 w-6 text-[#14274B] ml-1" fill="currentColor" />
                            </div>
                          </div>
                        )}
                      </div>
                      <span className="text-xs font-mono tracking-wider uppercase text-primary mb-1 block">
                        {item._type === "live" ? "Live" : item._type === "quiz" ? "Quiz" : "Ressource"}
                      </span>
                      <h3 className="font-heading text-lg group-hover:text-primary transition-colors">{item.title}</h3>
                      {item.excerpt && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.excerpt}</p>}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
